// src/components/UserSettings.js
import { useState } from 'react'
import { supabase } from '../supabaseClient'
import { C } from '../theme'
import { useAppPrefs } from '../AppPrefs'
import { t } from '../i18n'

const inp = (extra = {}) => ({
  width: '100%', padding: '10px 12px', borderRadius: 8,
  border: `1.5px solid ${C.border}`, background: C.panel,
  color: C.text, fontSize: 14, outline: 'none', boxSizing: 'border-box',
  ...extra,
})

const label = { fontSize: 12, fontWeight: 700, color: C.muted, marginBottom: 4, display: 'block', textTransform: 'uppercase', letterSpacing: 0.5 }

export default function UserSettings({ userProfile, onClose, showToast, onProfileUpdated }) {
  const isAdmin = userProfile?.role === 'admin'
  const [tab, setTab] = useState('profile') // 'profile' | 'password' | 'appearance' | 'dashboard'
  const { darkMode, setDarkMode, lang, setLang } = useAppPrefs()
  const _ = (key) => t(lang, key)

  // 基本資料
  const [name, setName] = useState(userProfile?.name || '')
  const [phone, setPhone] = useState(userProfile?.phone || '')
  const [saving, setSaving] = useState(false)

  // 修改密碼
  const [pwd, setPwd] = useState({ new: '', confirm: '' })
  const [pwdSaving, setPwdSaving] = useState(false)
  const [showPwd, setShowPwd] = useState(false)

  // 儀表板連結
  const [dashToken, setDashToken] = useState(null)
  const [dashLoading, setDashLoading] = useState(false)
  const [dashCopied, setDashCopied] = useState(false)

  const loadDashToken = async () => {
    if (!userProfile?.company_id) return
    setDashLoading(true)
    const { data } = await supabase.from('dashboard_tokens').select('token').eq('company_id', userProfile.company_id).maybeSingle()
    setDashToken(data?.token || null)
    setDashLoading(false)
  }

  const generateDashToken = async () => {
    if (!userProfile?.company_id) return
    setDashLoading(true)
    const { data, error } = await supabase.from('dashboard_tokens').upsert({ company_id: userProfile.company_id, label: '老闆儀表板' }, { onConflict: 'company_id' }).select('token').single()
    if (error) { showToast('產生失敗：' + error.message, 'error'); setDashLoading(false); return }
    setDashToken(data.token)
    setDashLoading(false)
    showToast('✓ 連結已產生')
  }

  const dashUrl = dashToken ? `${window.location.origin}?token=${dashToken}` : null

  const copyDashUrl = () => {
    if (!dashUrl) return
    navigator.clipboard.writeText(dashUrl).then(() => {
      setDashCopied(true)
      setTimeout(() => setDashCopied(false), 2000)
    })
  }

  const handleSaveProfile = async () => {
    if (!name.trim()) { showToast(_('fieldRequired'), 'error'); return }
    setSaving(true)
    try {
      const { error } = await supabase
        .from('user_profiles')
        .update({ name: name.trim(), phone: phone.trim() })
        .eq('id', userProfile.id)
      if (error) throw error
      showToast(_('profileSaved'))
      onProfileUpdated({ ...userProfile, name: name.trim(), phone: phone.trim() })
    } catch (e) {
      showToast('儲存失敗：' + e.message, 'error')
    } finally {
      setSaving(false)
    }
  }

  const handleChangePassword = async () => {
    if (!pwd.new) { showToast(_('pwdRequired'), 'error'); return }
    if (pwd.new.length < 6) { showToast(_('pwdTooShort'), 'error'); return }
    if (pwd.new !== pwd.confirm) { showToast(_('pwdMismatch'), 'error'); return }
    setPwdSaving(true)
    try {
      const { error } = await supabase.auth.updateUser({ password: pwd.new })
      if (error) throw error
      showToast(_('pwdSaved'))
      setPwd({ new: '', confirm: '' })
    } catch (e) {
      showToast('密碼更新失敗：' + e.message, 'error')
    } finally {
      setPwdSaving(false)
    }
  }

  const tabStyle = (key) => ({
    flex: 1, padding: '10px 0', background: 'none', border: 'none',
    borderBottom: tab === key ? `2.5px solid ${C.accent}` : '2.5px solid transparent',
    color: tab === key ? C.accent : C.muted,
    fontSize: 14, fontWeight: tab === key ? 700 : 500, cursor: 'pointer',
    transition: 'all 0.15s',
  })

  return (
    <>
      {/* Backdrop */}
      <div onClick={onClose} style={{
        position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.45)', zIndex: 1200,
      }} />

      {/* Drawer panel */}
      <div style={{
        position: 'fixed', top: 0, right: 0, bottom: 0, width: 360, maxWidth: '100vw',
        background: C.panel, zIndex: 1201,
        display: 'flex', flexDirection: 'column',
        boxShadow: '-4px 0 24px rgba(0,0,0,0.25)',
        animation: 'slideInRight 0.22s ease',
      }}>
        {/* Header */}
        <div style={{ padding: '20px 20px 16px', borderBottom: `1px solid ${C.border}`, display: 'flex', alignItems: 'center', gap: 12 }}>
          <div style={{ width: 44, height: 44, borderRadius: '50%', background: `linear-gradient(135deg,${C.accent},#0ea5e9)`, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 18, fontWeight: 900, color: '#fff', flexShrink: 0 }}>
            {(userProfile?.name || '?')[0].toUpperCase()}
          </div>
          <div style={{ flex: 1, overflow: 'hidden' }}>
            <div style={{ fontSize: 15, fontWeight: 800, color: C.text, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{userProfile?.name}</div>
            {userProfile?.employee_id && (
              <div style={{ fontSize: 11, color: C.muted, fontFamily: 'monospace' }}>工號 #{userProfile.employee_id}</div>
            )}
          </div>
          <button onClick={onClose} style={{ width: 36, height: 36, borderRadius: '50%', background: C.panel, border: 'none', color: C.muted, fontSize: 16, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>✕</button>
        </div>

        {/* Tabs */}
        <div style={{ display: 'flex', borderBottom: `1px solid ${C.border}`, flexShrink: 0, overflowX: 'auto' }}>
          <button style={tabStyle('profile')} onClick={() => setTab('profile')}>👤 {_('profile')}</button>
          <button style={tabStyle('password')} onClick={() => setTab('password')}>🔐 {_('password')}</button>
          <button style={tabStyle('appearance')} onClick={() => setTab('appearance')}>🎨 {_('appearance')}</button>
          {isAdmin && <button style={tabStyle('dashboard')} onClick={() => { setTab('dashboard'); loadDashToken() }}>📊 {_('dashboard')}</button>}
        </div>

        {/* Content */}
        <div style={{ flex: 1, overflowY: 'auto', padding: 20 }}>

          {tab === 'profile' && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 18 }}>
              {/* Read-only info */}
              <div style={{ background: C.bg, borderRadius: 10, padding: '12px 14px', display: 'flex', flexDirection: 'column', gap: 8 }}>
                <InfoRow label={_('employeeId')} value={userProfile?.employee_id || '—'} mono />
                <InfoRow label={_('department')} value={userProfile?.department || '—'} />
                <InfoRow label={_('position')} value={userProfile?.position || '—'} />
                <InfoRow label={_('loginAccount')} value={userProfile?.email || '—'} />
              </div>
              <div style={{ fontSize: 11, color: C.muted, marginTop: -8 }}>{_('adminNote')}</div>

              {/* Editable fields */}
              <div>
                <label style={label}>{_('displayName')}</label>
                <input style={inp()} value={name} onChange={e => setName(e.target.value)} placeholder={_('namePlaceholder')} maxLength={30} />
              </div>

              <div>
                <label style={label}>{_('phone')}</label>
                <input style={inp()} value={phone} onChange={e => setPhone(e.target.value)} placeholder={_('phonePlaceholder')} maxLength={20} type="tel" />
              </div>

              <button
                onClick={handleSaveProfile}
                disabled={saving}
                style={{ padding: '12px', borderRadius: 10, background: saving ? C.border : C.accent, border: 'none', color: '#fff', fontSize: 14, fontWeight: 700, cursor: saving ? 'not-allowed' : 'pointer', opacity: saving ? 0.7 : 1, transition: 'all 0.15s' }}>
                {saving ? _('saving') : _('saveProfile')}
              </button>
            </div>
          )}

          {tab === 'appearance' && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 24 }}>
              {/* Theme toggle */}
              <div>
                <div style={{ fontSize: 12, fontWeight: 700, color: C.muted, marginBottom: 10, textTransform: 'uppercase', letterSpacing: 0.5 }}>{_('themeLabel')}</div>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
                  {[
                    { value: false, icon: '☀️', label: _('light') },
                    { value: true,  icon: '🌙', label: _('dark') },
                  ].map(opt => (
                    <button
                      key={opt.label}
                      onClick={() => setDarkMode(opt.value)}
                      style={{
                        padding: '14px 12px', borderRadius: 12, cursor: 'pointer',
                        background: darkMode === opt.value ? C.accentDim : C.bg,
                        border: `2px solid ${darkMode === opt.value ? C.accent : C.border}`,
                        color: darkMode === opt.value ? C.accent : C.muted,
                        fontWeight: darkMode === opt.value ? 700 : 500,
                        fontSize: 13, transition: 'all 0.15s',
                        display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 6,
                      }}>
                      <span style={{ fontSize: 24 }}>{opt.icon}</span>
                      {opt.label}
                    </button>
                  ))}
                </div>
              </div>

              {/* Language selector */}
              <div>
                <div style={{ fontSize: 12, fontWeight: 700, color: C.muted, marginBottom: 10, textTransform: 'uppercase', letterSpacing: 0.5 }}>{_('languageLabel')}</div>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                  {[
                    { value: 'zh-TW', flag: '🇹🇼', label: '繁體中文' },
                    { value: 'en',    flag: '🇺🇸', label: 'English' },
                    { value: 'vi',    flag: '🇻🇳', label: 'Tiếng Việt' },
                  ].map(opt => (
                    <button
                      key={opt.value}
                      onClick={() => setLang(opt.value)}
                      style={{
                        padding: '12px 16px', borderRadius: 10, cursor: 'pointer',
                        background: lang === opt.value ? C.accentDim : C.bg,
                        border: `2px solid ${lang === opt.value ? C.accent : C.border}`,
                        color: lang === opt.value ? C.accent : C.text,
                        fontWeight: lang === opt.value ? 700 : 500,
                        fontSize: 14, transition: 'all 0.15s', textAlign: 'left',
                        display: 'flex', alignItems: 'center', gap: 12,
                      }}>
                      <span style={{ fontSize: 20 }}>{opt.flag}</span>
                      <span>{opt.label}</span>
                      {lang === opt.value && <span style={{ marginLeft: 'auto', fontSize: 16 }}>✓</span>}
                    </button>
                  ))}
                </div>
              </div>
            </div>
          )}

          {tab === 'dashboard' && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
              <div style={{ background: '#eff6ff', border: '1px solid #bfdbfe', borderRadius: 10, padding: '12px 14px' }}>
                <div style={{ fontSize: 13, color: '#1e40af', fontWeight: 700, marginBottom: 4 }}>📊 老闆專用儀表板</div>
                <div style={{ fontSize: 12, color: '#1e40af', lineHeight: 1.6 }}>產生一個不需要登入的唯讀連結，老闆點開就能看到庫存狀況、今日入出庫、低庫存警示等即時資訊。可傳到 LINE 或設為圖文選單連結。</div>
              </div>

              {dashLoading ? (
                <div style={{ textAlign: 'center', padding: 20, color: C.muted, fontSize: 13 }}>載入中...</div>
              ) : dashToken ? (
                <>
                  <div>
                    <div style={{ fontSize: 12, fontWeight: 700, color: C.muted, marginBottom: 6, textTransform: 'uppercase', letterSpacing: 0.5 }}>儀表板連結</div>
                    <div style={{ background: C.panel, border: `1.5px solid ${C.border}`, borderRadius: 8, padding: '10px 12px', fontSize: 12, color: C.muted, fontFamily: 'monospace', wordBreak: 'break-all', lineHeight: 1.5 }}>
                      {dashUrl}
                    </div>
                  </div>
                  <button onClick={copyDashUrl} style={{ padding: '12px', borderRadius: 10, background: dashCopied ? '#16a34a' : C.accent, border: 'none', color: '#fff', fontSize: 14, fontWeight: 700, cursor: 'pointer', transition: 'all 0.2s' }}>
                    {dashCopied ? '✓ 已複製！' : '📋 複製連結'}
                  </button>
                  <button onClick={() => window.open(dashUrl, '_blank')} style={{ padding: '12px', borderRadius: 10, background: 'none', border: `1.5px solid ${C.border}`, color: C.text, fontSize: 14, fontWeight: 600, cursor: 'pointer' }}>
                    🔗 預覽儀表板
                  </button>
                  <div style={{ background: '#fef9c3', border: '1px solid #fde047', borderRadius: 8, padding: '10px 12px' }}>
                    <div style={{ fontSize: 12, color: '#92400e', lineHeight: 1.5 }}>⚠️ 此連結不需要密碼，任何人點擊都可以查看庫存。請只傳給授權的人員。</div>
                  </div>
                  <button onClick={generateDashToken} style={{ padding: '10px', borderRadius: 8, background: 'none', border: `1.5px solid ${C.border}`, color: C.muted, fontSize: 13, cursor: 'pointer' }}>
                    🔄 重新產生連結（舊連結失效）
                  </button>
                </>
              ) : (
                <button onClick={generateDashToken} style={{ padding: '14px', borderRadius: 10, background: C.accent, border: 'none', color: '#fff', fontSize: 15, fontWeight: 700, cursor: 'pointer' }}>
                  ✨ 產生儀表板連結
                </button>
              )}
            </div>
          )}

          {tab === 'password' && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 18 }}>
              <div style={{ background: C.yellowBg, border: `1px solid ${C.yellowBorder}`, borderRadius: 10, padding: '12px 14px' }}>
                <div style={{ fontSize: 13, color: C.yellow, fontWeight: 600, marginBottom: 4 }}>⚠️</div>
                <div style={{ fontSize: 12, color: C.yellow, lineHeight: 1.5 }}>{_('pwdReminder')}</div>
              </div>

              <div>
                <label style={label}>{_('newPassword')}</label>
                <div style={{ position: 'relative' }}>
                  <input
                    style={inp({ paddingRight: 44 })}
                    type={showPwd ? 'text' : 'password'}
                    value={pwd.new}
                    onChange={e => setPwd(p => ({ ...p, new: e.target.value }))}
                    placeholder={_('newPasswordPlaceholder')}
                  />
                  <button onClick={() => setShowPwd(v => !v)} style={{ position: 'absolute', right: 10, top: '50%', transform: 'translateY(-50%)', background: 'none', border: 'none', cursor: 'pointer', color: C.muted, fontSize: 16, padding: 4 }}>
                    {showPwd ? '🙈' : '👁️'}
                  </button>
                </div>
                {pwd.new && (
                  <div style={{ marginTop: 6, display: 'flex', gap: 4 }}>
                    {['length', 'mixed'].map(k => {
                      const ok = k === 'length' ? pwd.new.length >= 8 : /[A-Za-z]/.test(pwd.new) && /[0-9]/.test(pwd.new)
                      return <span key={k} style={{ fontSize: 11, padding: '2px 8px', borderRadius: 20, background: ok ? C.greenBg : C.redBg, color: ok ? C.green : C.red, fontWeight: 600 }}>
                        {k === 'length' ? (ok ? _('pwdCheck8') : _('pwdCheckFail8')) : (ok ? _('pwdCheckMixed') : _('pwdCheckFailMixed'))}
                      </span>
                    })}
                  </div>
                )}
              </div>

              <div>
                <label style={label}>{_('confirmPassword')}</label>
                <input
                  style={inp({ borderColor: pwd.confirm && pwd.confirm !== pwd.new ? C.red : C.border })}
                  type={showPwd ? 'text' : 'password'}
                  value={pwd.confirm}
                  onChange={e => setPwd(p => ({ ...p, confirm: e.target.value }))}
                  placeholder={_('confirmPasswordPlaceholder')}
                />
                {pwd.confirm && pwd.confirm !== pwd.new && (
                  <div style={{ fontSize: 11, color: C.red, marginTop: 4 }}>{_('pwdMismatch')}</div>
                )}
              </div>

              <button
                onClick={handleChangePassword}
                disabled={pwdSaving || !pwd.new || !pwd.confirm}
                style={{ padding: '12px', borderRadius: 10, background: (pwdSaving || !pwd.new || !pwd.confirm) ? C.border : '#7c3aed', border: 'none', color: (pwdSaving || !pwd.new || !pwd.confirm) ? C.muted : '#fff', fontSize: 14, fontWeight: 700, cursor: (pwdSaving || !pwd.new || !pwd.confirm) ? 'not-allowed' : 'pointer', transition: 'all 0.15s' }}>
                {pwdSaving ? _('changingPassword') : _('changePassword')}
              </button>
            </div>
          )}
        </div>
      </div>

      <style>{`@keyframes slideInRight { from { transform: translateX(100%) } to { transform: translateX(0) } }`}</style>
    </>
  )
}

function InfoRow({ label: lbl, value, mono = false }) {
  return (
    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8 }}>
      <span style={{ fontSize: 12, color: C.muted, flexShrink: 0 }}>{lbl}</span>
      <span style={{ fontSize: 13, color: C.text, fontWeight: 600, fontFamily: mono ? 'monospace' : undefined, textAlign: 'right', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{value}</span>
    </div>
  )
}
